import { useEffect } from 'react';
import { MetricsCard } from '@/components/common/MetricsCard';
import { Card } from '@/components/common/Card';
import {
  ChartMultiple24Regular,
  Money24Regular,
  Building24Regular,
  ArrowTrendingLines24Regular,
} from '@fluentui/react-icons';
import { Spinner } from '@fluentui/react-components';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import { useProjectStore } from '@/stores/projectStore';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FINANCIAL_COLORS } from '@/utils/chartColors';

export function Dashboard() {
  const {
    currentProject,
    currentCalculations,
    isLoading,
    error,
    fetchProjects,
    fetchCalculations,
  } = useProjectStore();

  useEffect(() => {
    // Fetch projects on mount
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    // Fetch calculations for sample project
    if (currentProject?.id) {
      fetchCalculations(currentProject.id);
    }
  }, [currentProject?.id, fetchCalculations]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="large" label="Loading project data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-error-red text-lg mb-2">Error loading data</p>
          <p className="text-gray-90">{error}</p>
        </div>
      </div>
    );
  }

  const metrics = currentCalculations
    ? {
        leveredIRR: currentCalculations.returnMetrics.leveredIRR,
        totalCost: currentCalculations.totalProjectCost,
        stabilizedNOI: currentCalculations.stabilizedNOI,
        noiCap: currentCalculations.noiCapAtStabilization,
        yieldOnCost: currentCalculations.yieldOnCost,
        equityMultiple: currentCalculations.returnMetrics.equityMultiple,
        unleveredIRR: currentCalculations.returnMetrics.unleveredIRR,
        totalEquity: currentCalculations.totalEquityInvested,
        totalDebt: currentCalculations.totalDebtFinancing,
      }
    : null;

  return (
    <div className="space-y-[35px]">
      {/* Header */}
      <div className="mb-[35px]">
        <h1 className="text-[36px] font-light text-dark-text mb-2">
          Dashboard
        </h1>
        <p className="text-gray-300 text-sm">
          {currentProject?.name || 'Real Estate Development Model'}
          {currentProject?.city && currentProject?.state && (
            <> • {currentProject.city}, {currentProject.state}</>
          )}
          {currentProject?.analysis_start_date && (
            <> • Analysis Start: {new Date(currentProject.analysis_start_date).toLocaleDateString()}</>
          )}
        </p>
      </div>

      {!metrics ? (
        <Card title="No Calculations Available">
          <p className="text-gray-90">
            Run calculations to see project metrics and analysis.
          </p>
        </Card>
      ) : (
        <>
          {/* Key Metrics Grid - 2 rows x 3 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px] mb-[50px]">
            <MetricsCard
              title="Levered IRR"
              value={formatPercentage(metrics.leveredIRR)}
              subtitle="Internal Rate of Return"
              icon={<ArrowTrendingLines24Regular />}
            />

            <MetricsCard
              title="Total Project Cost"
              value={formatCurrency(metrics.totalCost)}
              subtitle="All-in Development Cost"
              icon={<Money24Regular />}
            />

            <MetricsCard
              title="Yield on Cost"
              value={formatPercentage(metrics.yieldOnCost)}
              subtitle="NOI / Total Project Cost"
              icon={<Building24Regular />}
            />

            <MetricsCard
              title="Stabilized NOI"
              value={formatCurrency(metrics.stabilizedNOI)}
              subtitle="Net Operating Income"
              icon={<Money24Regular />}
            />

            <MetricsCard
              title="Equity Multiple"
              value={`${metrics.equityMultiple.toFixed(2)}x`}
              subtitle="Total Return Multiple"
              icon={<ChartMultiple24Regular />}
            />

            <MetricsCard
              title="Exit Cap Rate"
              value={formatPercentage(metrics.noiCap)}
              subtitle="Reversion Cap Rate"
              icon={<ArrowTrendingLines24Regular />}
            />
          </div>
        </>
      )}

      {/* Project Timeline */}
      <Card title="Project Timeline" subtitle="Key milestones and phases">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-gray-90">Construction</div>
            <div className="flex-1 bg-gray-20 rounded-full h-8 relative overflow-hidden">
              <div
                className="bg-microsoft-blue h-full rounded-full"
                style={{ width: '100%' }}
              ></div>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                Completed
              </span>
            </div>
            <div className="w-24 text-sm text-gray-90 text-right">
              Dec 2025
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-gray-90">Lease-up</div>
            <div className="flex-1 bg-gray-20 rounded-full h-8 relative overflow-hidden">
              <div
                className="bg-success-green h-full rounded-full"
                style={{ width: '65%' }}
              ></div>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                65% Leased
              </span>
            </div>
            <div className="w-24 text-sm text-gray-90 text-right">
              Jun 2026
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-gray-90">Stabilization</div>
            <div className="flex-1 bg-gray-20 rounded-full h-8 relative overflow-hidden">
              <div
                className="bg-gray-50 h-full rounded-full"
                style={{ width: '0%' }}
              ></div>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-90">
                Pending
              </span>
            </div>
            <div className="w-24 text-sm text-gray-90 text-right">
              Dec 2026
            </div>
          </div>
        </div>
      </Card>

      {/* Cash Flow Chart */}
      {metrics && currentCalculations && (
        <Card
          title="Cash Flow Projection"
          subtitle="Annual cash flows over investment period"
        >
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={currentCalculations.annualCashFlows.map((cf) => ({
              year: `Year ${cf.year}`,
              Revenue: cf.revenue / 1000000,
              Expenses: cf.expenses / 1000000,
              NOI: cf.noi / 1000000,
              'Cash Flow': cf.cashFlow / 1000000,
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E9" vertical={false} />
              <XAxis
                dataKey="year"
                stroke="#999"
                tick={{ fontSize: 11, fill: '#999' }}
                axisLine={{ stroke: '#d0d0d0' }}
              />
              <YAxis
                stroke="#999"
                tick={{ fontSize: 11, fill: '#999' }}
                axisLine={{ stroke: '#d0d0d0' }}
                label={{
                  value: 'Amount ($M)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: '#999', fontSize: 11 }
                }}
              />
              <Tooltip
                formatter={(value: number) => `$${value.toFixed(2)}M`}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  fontSize: 12
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Revenue" fill={FINANCIAL_COLORS.revenue} radius={[2, 2, 0, 0]} />
              <Bar dataKey="Expenses" fill={FINANCIAL_COLORS.expenses} radius={[2, 2, 0, 0]} />
              <Bar dataKey="NOI" fill={FINANCIAL_COLORS.noi} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Quick Stats */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card title="Property Details">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Total NRA</span>
                <span className="text-sm font-semibold">
                  {currentProject?.total_nra?.toLocaleString() || 'N/A'} SF
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Retail Type</span>
                <span className="text-sm font-semibold capitalize">
                  {currentProject?.retail_type?.replace('_', ' ') || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Location</span>
                <span className="text-sm font-semibold">
                  {currentProject?.city && currentProject?.state
                    ? `${currentProject.city}, ${currentProject.state}`
                    : 'N/A'}
                </span>
              </div>
            </div>
          </Card>

          <Card title="Financial Summary">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Equity Investment</span>
                <span className="text-sm font-semibold">
                  {formatCurrency(metrics.totalEquity)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Debt Financing</span>
                <span className="text-sm font-semibold">
                  {formatCurrency(metrics.totalDebt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">LTC</span>
                <span className="text-sm font-semibold">
                  {formatPercentage(
                    metrics.totalDebt / (metrics.totalEquity + metrics.totalDebt)
                  )}
                </span>
              </div>
            </div>
          </Card>

          <Card title="Returns">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Equity Multiple</span>
                <span className="text-sm font-semibold">
                  {metrics.equityMultiple.toFixed(2)}x
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Unlevered IRR</span>
                <span className="text-sm font-semibold">
                  {formatPercentage(metrics.unleveredIRR)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-90">Stabilized NOI</span>
                <span className="text-sm font-semibold">
                  {formatCurrency(metrics.stabilizedNOI)}
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
